<template>
  <van-dialog
    :value="show"
    @input="onDialogInput"
    title="课程未解锁"
    :show-confirm-button="!showBackButton && !loading"
    confirm-button-text="我知道了"
    confirm-button-color="#d51900"
    :close-on-click-overlay="!showBackButton && !loading"
    class="unlock-dialog"
    get-container="body"
    :lock-scroll="false"
  >
    <div class="unlock-tip-content">
      <!-- 骨架屏 -->
      <div class="unlock-skeleton" v-if="loading">
        <div class="skeleton-icon"></div>
        <div class="skeleton-line skeleton-rule"></div>
        <div class="skeleton-line skeleton-progress"></div>
        <div class="skeleton-course-list">
          <div class="skeleton-course-item" v-for="i in 3" :key="i">
            <div class="skeleton-dot"></div>
            <div class="skeleton-line skeleton-course-name"></div>
            <div class="skeleton-line skeleton-course-action"></div>
          </div>
        </div>
        <div class="skeleton-line skeleton-hint"></div>
      </div>

      <!-- 实际内容 -->
      <template v-else>
        <!-- 锁图标 -->
        <div class="unlock-icon-large">🔐</div>

        <!-- 概要信息 -->
        <p class="unlock-rule">{{ unlockData.UnlockRuleDesc || '需完成前置课程后解锁' }}</p>

        <!-- 多规则显示 -->
        <div v-if="hasMultipleRules" class="rules-container">
          <div
            v-for="(rule, ruleIdx) in unlockData.UnlockRules"
            :key="rule.RuleId"
            class="rule-block"
            :class="{ 'rule-satisfied': rule.IsSatisfied }"
          >
            <div class="rule-header">
              <span class="rule-status-dot" :class="rule.IsSatisfied ? 'dot-green' : 'dot-orange'"></span>
              <span class="rule-desc-inline">{{ rule.UnlockTypeDesc }}</span>
            </div>

            <!-- 该规则的前置课程 -->
            <div class="precourse-items" v-if="rule.PreCurriculumList && rule.PreCurriculumList.length > 0">
              <div
                v-for="course in rule.PreCurriculumList"
                :key="course.Id"
                class="precourse-item"
                :class="{ 'completed': course.IsCompleted }"
                @click="goToCourse(course)"
              >
                <span class="status-dot" :class="course.IsCompleted ? 'dot-green' : 'dot-red'"></span>
                <span class="precourse-name">{{ course.Subject }}</span>
                <span class="precourse-action">
                  <span v-if="course.IsCompleted" class="action-done">✓ 已完成</span>
                  <span v-else class="action-go">去学习 ›</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 单规则显示（保持原有样式） -->
        <div v-else-if="hasCourseList" class="precourse-list">
          <p class="precourse-title">📋 前置课程完成情况：</p>
          <div class="precourse-items">
            <div
              v-for="course in displayCourseList"
              :key="course.Id"
              class="precourse-item"
              :class="{ 'completed': course.IsCompleted }"
              @click="goToCourse(course)"
            >
              <span class="status-dot" :class="course.IsCompleted ? 'dot-green' : 'dot-red'"></span>
              <span class="precourse-name">{{ course.Subject }}</span>
              <span class="precourse-action">
                <span v-if="course.IsCompleted" class="action-done">✓ 已完成</span>
                <span v-else class="action-go">去学习 ›</span>
              </span>
            </div>
          </div>
        </div>

        <p class="unlock-hint">完成上述课程后，此课程将自动解锁</p>

        <!-- 返回按钮（仅在课程学习页面被拦截时显示） -->
        <div v-if="showBackButton" class="dialog-actions">
          <button class="btn-back" @click="onBack">返回上一页</button>
        </div>
      </template>
    </div>
  </van-dialog>
</template>

<script>
export default {
  name: 'UnlockTipDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    unlockData: {
      type: Object,
      default: () => ({})
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      scrollTop: 0,
      scrollLocked: false,
      bodyStyleSnapshot: null
    }
  },
  computed: {
    hasMultipleRules () {
      return this.unlockData.UnlockRules && this.unlockData.UnlockRules.length > 1
    },
    hasCourseList () {
      // 单规则时使用 PreCurriculumList 或 UnlockRules[0].PreCurriculumList
      if (this.unlockData.PreCurriculumList && this.unlockData.PreCurriculumList.length > 0) {
        return true
      }
      if (this.unlockData.UnlockRules && this.unlockData.UnlockRules.length === 1) {
        return this.unlockData.UnlockRules[0].PreCurriculumList && this.unlockData.UnlockRules[0].PreCurriculumList.length > 0
      }
      return false
    },
    displayCourseList () {
      if (this.unlockData.PreCurriculumList && this.unlockData.PreCurriculumList.length > 0) {
        return this.unlockData.PreCurriculumList
      }
      if (this.unlockData.UnlockRules && this.unlockData.UnlockRules.length === 1) {
        return this.unlockData.UnlockRules[0].PreCurriculumList || []
      }
      return []
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.lockScroll()
      } else {
        this.unlockScroll()
      }
    }
  },
  methods: {
    lockScroll () {
      if (this.scrollLocked) {
        return
      }
      // 记录当前滚动位置
      this.scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
      this.bodyStyleSnapshot = {
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow
      }
      this.scrollLocked = true
      // 锁定 body
      document.body.style.position = 'fixed'
      document.body.style.top = `-${this.scrollTop}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
    },
    unlockScroll () {
      if (!this.scrollLocked) {
        return
      }
      // 恢复 body
      document.body.style.position = this.bodyStyleSnapshot ? this.bodyStyleSnapshot.position : ''
      document.body.style.top = this.bodyStyleSnapshot ? this.bodyStyleSnapshot.top : ''
      document.body.style.width = this.bodyStyleSnapshot ? this.bodyStyleSnapshot.width : ''
      document.body.style.overflow = this.bodyStyleSnapshot ? this.bodyStyleSnapshot.overflow : ''
      this.bodyStyleSnapshot = null
      this.scrollLocked = false
      // 恢复滚动位置
      window.scrollTo(0, this.scrollTop)
    },
    goToCourse (course) {
      this.$emit('update:show', false)
      this.$emit('go-course', course)
    },
    onBack () {
      this.$emit('update:show', false)
      this.$emit('back')
    },
    onDialogInput (val) {
      if (!val && this.showBackButton) {
        // 如果是拦截模式，不允许通过点击遮罩关闭
        return
      }
      this.$emit('update:show', val)
    }
  },
  beforeDestroy () {
    this.unlockScroll()
  }
}
</script>

<style scoped>
.unlock-tip-content {
  padding: 20px 16px 24px;
  text-align: center;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* 骨架屏样式 */
.unlock-skeleton {
  padding: 10px 0;
}

.skeleton-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin: 0 auto 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-rule {
  height: 18px;
  width: 70%;
  margin: 0 auto 8px;
}

.skeleton-progress {
  height: 14px;
  width: 40%;
  margin: 0 auto 16px;
}

.skeleton-course-list {
  background: #f8f9fa;
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 16px;
}

.skeleton-course-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  margin-bottom: 8px;
}

.skeleton-course-item:last-child {
  margin-bottom: 0;
}

.skeleton-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-course-name {
  flex: 1;
  height: 14px;
}

.skeleton-course-action {
  width: 50px;
  height: 14px;
  flex-shrink: 0;
}

.skeleton-hint {
  height: 12px;
  width: 60%;
  margin: 0 auto;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.unlock-icon-large {
  font-size: 48px;
  margin-bottom: 12px;
}

.unlock-rule {
  font-size: 16px;
  color: #333;
  margin: 0 0 6px;
  font-weight: 600;
}

.unlock-progress {
  font-size: 14px;
  color: #d51900;
  margin: 0 0 16px;
  font-weight: 500;
}

/* 多规则容器 */
.rules-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.rule-block {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 14px;
  padding: 14px;
  text-align: left;
  border: 1px solid rgba(213, 25, 0, 0.08);
  transition: all 0.2s ease;
}

.rule-block.rule-satisfied {
  background: linear-gradient(135deg, #f0fff4 0%, #e6ffed 100%);
  border-color: rgba(82, 196, 26, 0.2);
}

.rule-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.rule-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-orange {
  background: #fa8c16;
  box-shadow: 0 0 0 0 rgba(250, 140, 22, 0.7);
  animation: pulse-orange 1.5s infinite;
}

@keyframes pulse-orange {
  0% { box-shadow: 0 0 0 0 rgba(250, 140, 22, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(250, 140, 22, 0); }
  100% { box-shadow: 0 0 0 0 rgba(250, 140, 22, 0); }
}

.rule-desc-inline {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

/* 前置课程列表 */
.precourse-list {
  background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
  border-radius: 16px;
  padding: 14px;
  text-align: left;
  margin-bottom: 16px;
}

.precourse-title {
  font-size: 14px;
  color: #5a6a7a;
  margin: 0 0 10px;
  font-weight: 600;
}

.precourse-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.precourse-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px;
  background: #fff;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.precourse-item:active {
  transform: scale(0.98);
  background: #f5f7fa;
}

.precourse-item.completed {
  opacity: 0.7;
}

/* 呼吸灯效果 */
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-red {
  background: #ff4d4f;
  box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
  animation: pulse-red 1.5s infinite;
}

.dot-green {
  background: #52c41a;
  box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
  animation: pulse-green 2s infinite;
}

@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(255, 77, 79, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
}

@keyframes pulse-green {
  0% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(82, 196, 26, 0); }
  100% { box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
}

.precourse-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-weight: 500;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.precourse-action {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
}

.action-go {
  color: #d51900;
}

.action-done {
  color: #52c41a;
}

.unlock-hint {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0;
}

.dialog-actions {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f4f8;
}

.btn-back {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #d51900 0%, #ff4d3a 100%);
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(213, 25, 0, 0.3);
}

.btn-back:active {
  transform: scale(0.98);
  opacity: 0.9;
}
</style>

<style>
/* 全局样式 - 修复弹窗闪白边和位置偏移 */
.van-dialog {
  position: fixed !important;
  overflow: hidden;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translate3d(-50%, -50%, 0) !important;
  top: 50% !important;
  left: 50% !important;
  margin: 0 !important;
}

.van-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.7) !important;
}

.van-dialog__content {
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/UnlockTipDialog.vue